import { GraphqlClient } from 'src/api/graphql_client';
import { getPlatform } from 'src/config/environment';
import { UserInfo } from 'src/hooks/use_auth';
import { OrganizationFieldsFragment } from 'src/types';
import { DistributionPlatform } from 'src/types/env';

/**
 * Singleton service that provides access to the user info and effective organization.
 */
export class AuthenticationService {
  private static instance: AuthenticationService | undefined;
  private user: UserInfo | undefined;
  private effectiveOrganization: OrganizationFieldsFragment | undefined;
  /** LDT CS/Sales selected org name sent as the Organization request header. */
  private organizationHeader: string | null = null;

  private constructor() {
    // Private constructor for singleton
  }

  /**
   * Initialize the authentication service with the user info.
   * @param userInfo - The user info
   */
  public static initialize(userInfo: UserInfo) {
    if (!this.instance) {
      this.instance = new AuthenticationService();
    }

    this.instance.user = userInfo;
  }

  /**
   * Get the user info
   */
  getUserInfo(): UserInfo {
    if (this.user === undefined) {
      throw new Error('User not initialized. Call initialize() first.');
    }

    return this.user;
  }

  /**
   * Gets the effective organization for the current user.
   * - CS/Sales with a selected org → selected org
   * - Otherwise → user's organization
   */
  getEffectiveOrganization(): OrganizationFieldsFragment {
    if (this.effectiveOrganization === undefined) {
      throw new Error(
        'Effective organization not set. Resolve it via getEffectiveOrganization query first.',
      );
    }

    return this.effectiveOrganization;
  }

  /**
   * Stores the effective organization returned by the API.
   */
  setEffectiveOrganization(organization: OrganizationFieldsFragment): void {
    this.effectiveOrganization = organization;
  }

  /**
   * Organization name currently set on the GraphQL Organization header (LDT).
   * Null until the first successful org resolution sets a header.
   */
  getOrganizationHeader(): string | null {
    return this.organizationHeader;
  }

  /**
   * Sets the Organization GraphQL header used for LDT CS/Sales org switching.
   * Must not be called for Non-CS or SaMD users.
   */
  setOrganizationHeader(organizationName: string): void {
    if (getPlatform() !== DistributionPlatform.Ldt) {
      throw new Error('Organization selection is only supported on Ldt');
    }

    this.organizationHeader = organizationName;
    GraphqlClient.setDefaultHeaders({ Organization: organizationName });
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): AuthenticationService {
    if (this.instance === undefined) {
      throw new Error(
        'AuthenticationService not initialized. Call initialize() first.',
      );
    }

    return this.instance;
  }
}
